import type { NotificationFilter, PortalNotification } from "@/lib/types";

/** How the active filter chip is named in the confirmation dialog's scope line. */
const FILTER_LABEL: Record<NotificationFilter, string> = {
  all: "All",
  action_required: "Action required",
  unread: "Unread",
  billing: "Billing",
  domains: "Domains",
  website: "Weavex",
  hosting: "Hosting",
};

export interface BulkDismissPreview {
  /** The keys this would dismiss, captured once (at click time) so a later change to `shown` can't alter it. */
  keys: string[];
  count: number;
  /** "Dismiss 37 notifications?" */
  title: string;
  /** What this affects, named by the filter that produced `shown`. */
  scope: string;
}

/**
 * What "Dismiss read notifications" in the Bulk menu would do right now: the read notifications among
 * `shown`, under the filter that listed them (PHP-3355). Pure so the count and scope text are testable
 * on their own, and so the caller can capture the result at click time before opening the confirm dialog.
 */
export function previewBulkDismiss(
  shown: PortalNotification[],
  filter: NotificationFilter,
): BulkDismissPreview {
  const keys = shown.filter((n) => n.read).map((n) => n.key);
  const count = keys.length;
  const noun = count === 1 ? "notification" : "notifications";
  const scopePhrase =
    filter === "all" ? "currently shown" : `currently shown under the “${FILTER_LABEL[filter]}” filter`;
  return {
    keys,
    count,
    title: `Dismiss ${count} ${noun}?`,
    scope: `This dismisses the ${count} read ${noun} ${scopePhrase}.`,
  };
}
